import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography, CircularProgress, Paper, Alert, Link, TextField, Button, Snackbar } from "@mui/material";
import { useScrapedPageQuery, useStartSinglePageAnalysis } from "./hooks";
import { marked } from "marked";

export default function BrandScrapedPage() {
  const { id, pageId } = useParams();
  const { data, isLoading, error } = useScrapedPageQuery(id, pageId);
  const startSinglePageAnalysis = useStartSinglePageAnalysis(id, pageId);
  const [promptsModified, setPromptsModified] = useState(false);
  const [prompts, setPrompts] = useState({
    discoveryPrompt: "",
    analysisPrompt: ""
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  useEffect(() => {
    if (data?.page && !promptsModified) {
      setPrompts({
        discoveryPrompt: data.page.discoveryPrompt || data.crawledSite?.discoveryPrompt || "",
        analysisPrompt: data.page.analysisPrompt || data.crawledSite?.analysisPrompt || ""
      });
    }
  }, [data, promptsModified]);

  const handlePromptChange = (field) => (event) => {
    setPromptsModified(true);
    setPrompts(prev => ({ ...prev, [field]: event.target.value }));
  };

  const startAnalysis = async () => {
    try {
      await startSinglePageAnalysis.mutateAsync(prompts);
      setSnackbar({
        open: true,
        message: "Analysis started successfully",
        severity: "success"
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to start analysis",
        severity: "error"
      });
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box m={4}>
        <Alert severity="error">
          Error loading page: {error.message}
        </Alert>
      </Box>
    );
  }

  if (!data?.page) {
    return (
      <Box m={4}>
        <Alert severity="warning">
          Page not found
        </Alert>
      </Box>
    );
  }

  const { page, crawledSite, brandName } = data;

  return (
    <Box m={4}>
      <Box mb={4}>
        <Box mb={2}>
          <Link href={`/brands/${id}`} sx={{ textDecoration: "none" }}>
            <Typography variant="body1" color="primary">
              ← Back to {brandName}
            </Typography>
          </Link>
        </Box>
        <Typography variant="h2" gutterBottom>
          {page.metadata?.title || "Untitled Page"}
        </Typography>
        <Link href={page.metadata?.url} target="_blank" rel="noopener noreferrer">
          {page.metadata?.url}
        </Link>
        <Typography variant="body2" color="textSecondary" mt={1}>
          Scraped at: {new Date(page.scrapedAt).toLocaleString()}
        </Typography>
        <Alert severity="info" sx={{ mt: 2 }}>
          {page.hasAiDiscoveredSustainabilityContent
            ? (
              "This page contains sustainability-related content"
            )
            : (
              "No sustainability-related content was discovered"
            )}
        </Alert>
      </Box>
      <Paper elevation={2} sx={{ p: 3, mt: 4, mb: 4 }}>
        <Typography variant="h3" gutterBottom>
          AI Discovery
        </Typography>
        <Box mb={3}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Discovery Prompt"
            defaultValue={page?.discoveryPrompt || crawledSite?.discoveryPrompt}
            sx={{ mb: 2 }}
            onChange={handlePromptChange("discoveryPrompt")}
          />
        </Box>
        <Box
          dangerouslySetInnerHTML={{ __html: marked(page.discoveryAnalysis || "") }}
          sx={{
            "& h1": { fontSize: "2rem", mt: 3, mb: 2 },
            "& h2": { fontSize: "1.8rem", mt: 3, mb: 2 },
            "& h3": { fontSize: "1.6rem", mt: 2, mb: 1 },
            "& p": { mb: 2 },
            "& ul, & ol": { mb: 2, pl: 3 },
            "& li": { mb: 1 },
            "& a": { color: "primary.main" }
          }}
        />
      </Paper>
      <Paper elevation={2} sx={{ p: 3, mt: 4, mb: 4 }}>
        <Typography variant="h3" gutterBottom>
          AI Analysis
        </Typography>
        <Box mb={3}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Analysis Prompt"
            defaultValue={page?.analysisPrompt || crawledSite?.analysisPrompt}
            sx={{ mb: 2 }}
            onChange={handlePromptChange("analysisPrompt")}
          />
          <Button
            variant="contained"
            onClick={startAnalysis}
            disabled={startSinglePageAnalysis.isPending}
          >
            {startSinglePageAnalysis.isPending ? "Analyzing..." : "Re-Analyze"}
          </Button>
        </Box>
        <Box
          dangerouslySetInnerHTML={{ __html: marked(page.aiAnalysis || "") }}
          sx={{
            "& h1": { fontSize: "2rem", mt: 3, mb: 2 },
            "& h2": { fontSize: "1.8rem", mt: 3, mb: 2 },
            "& h3": { fontSize: "1.6rem", mt: 2, mb: 1 },
            "& p": { mb: 2 },
            "& ul, & ol": { mb: 2, pl: 3 },
            "& li": { mb: 1 },
            "& a": { color: "primary.main" }
          }}
        />
      </Paper>
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h3" gutterBottom>
          Page Content
        </Typography>
        <Box
          dangerouslySetInnerHTML={{ __html: marked(page.markdown || "") }}
          sx={{
            "& h1": { fontSize: "2rem", mt: 3, mb: 2 },
            "& h2": { fontSize: "1.8rem", mt: 3, mb: 2 },
            "& h3": { fontSize: "1.6rem", mt: 2, mb: 1 },
            "& p": { mb: 2 },
            "& ul, & ol": { mb: 2, pl: 3 },
            "& li": { mb: 1 },
            "& a": { color: "primary.main" }
          }}
        />
      </Paper>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
